import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { FiEye, FiEdit2, FiSend, FiTrash2, FiPlusCircle, FiFileText } from 'react-icons/fi';
import api from '../../utils/api';

const font = "'Outfit', sans-serif";
const BL = '#6366f1';
const C = { text: '#1e1b4b', sub: '#6b7280', border: '#e0e7ff', bg: '#eef2ff', white: '#fff' };

const statusBadge = (status) => {
    const map = { draft: ['#f59e0b', '#fffbeb'], submitted: ['#6366f1', '#eef2ff'], approved: ['#10b981', '#ecfdf5'], rejected: ['#ef4444', '#fef2f2'] };
    const [color, bg] = map[status] || ['#6b7280', '#f9fafb'];
    return <span style={{ padding: '4px 12px', borderRadius: 20, background: bg, color, fontSize: 11, fontWeight: 700, fontFamily: font, textTransform: 'capitalize' }}>{status}</span>;
};

const MyPapers = () => {
    const [papers, setPapers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const navigate = useNavigate();
    const { hasAssignments } = useOutletContext() || { hasAssignments: false };

    const fetch = () => {
        api.get('/api/teacher/papers')
            .then(r => setPapers(r.data.papers || []))
            .catch(() => { })
            .finally(() => setLoading(false));
    };
    useEffect(fetch, []);

    const handleSubmit = async (id) => {
        if (!window.confirm('Submit this paper for admin review?')) return;
        await api.put(`/api/teacher/papers/${id}/submit`);
        fetch();
    };
    const handleDelete = async (id) => {
        if (!window.confirm('Delete this draft paper?')) return;
        await api.delete(`/api/teacher/papers/${id}`);
        fetch();
    };

    const filtered = filter === 'all' ? papers : papers.filter(p => p.status === filter);

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 14 }}>
            <div style={{ width: 36, height: 36, border: '4px solid #e0e7ff', borderTopColor: BL, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ color: C.sub, fontFamily: font }}>Loading papers…</p>
        </div>
    );

    return (
        <div style={{ width: '100%', fontFamily: font }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
                <h1 style={{ fontSize: 20, fontWeight: 900, color: C.text, margin: 0 }}>📋 My Exam Papers</h1>
                {hasAssignments && (
                    <button onClick={() => navigate('/teacher/create-paper')} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#818cf8,#6366f1)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: font, boxShadow: '0 4px 14px rgba(99,102,241,0.25)' }}>
                        <FiPlusCircle size={15} /> New Paper
                    </button>
                )}
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
                {['all', 'draft', 'submitted', 'approved', 'rejected'].map(f => (
                    <button key={f} onClick={() => setFilter(f)} style={{ padding: '6px 16px', borderRadius: 20, border: `1.5px solid ${filter === f ? BL : C.border}`, background: filter === f ? BL : C.white, color: filter === f ? '#fff' : C.sub, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: font, textTransform: 'capitalize' }}>
                        {f === 'all' ? `All (${papers.length})` : f}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div style={{ background: C.white, borderRadius: 18, border: `1.5px solid ${C.border}`, padding: '60px 24px', textAlign: 'center' }}>
                    <FiFileText size={48} color="#c7d2fe" style={{ marginBottom: 16 }} />
                    <h3 style={{ color: C.text, fontWeight: 700, margin: '0 0 8px' }}>No papers yet</h3>
                    <p style={{ color: C.sub, fontSize: 13, margin: '0 0 20px' }}>{hasAssignments ? 'Create your first exam paper to get started.' : 'You have not been assigned any batches by the admin yet so you cannot create papers.'}</p>
                    {hasAssignments && (
                        <button onClick={() => navigate('/teacher/create-paper')} style={{ padding: '10px 22px', borderRadius: 12, border: 'none', background: BL, color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: font }}>
                            Create Paper
                        </button>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {filtered.map(p => (
                        <div
                            key={p.id}
                            onClick={() => navigate(`/teacher/papers/${p.id}`)}
                            style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`, padding: '16px 20px', boxShadow: '0 2px 10px rgba(99,102,241,0.07)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', cursor: 'pointer', transition: 'all 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#818cf8,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                                <FiFileText size={18} />
                            </div>
                            <div style={{ flex: 1, minWidth: 180 }}>
                                <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 4 }}>{p.title}</div>
                                <div style={{ fontSize: 12, color: C.sub }}>{p.subject_name} &nbsp;·&nbsp; {p.batch_name} &nbsp;·&nbsp; Sem {p.semester} &nbsp;·&nbsp; {p.exam_type}</div>
                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{p.question_count} questions &nbsp;·&nbsp; {p.total_marks} marks &nbsp;·&nbsp; {p.duration_mins} mins</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {statusBadge(p.status)}
                                {p.status === 'draft' && (<>
                                    <button onClick={(e) => { e.stopPropagation(); navigate(`/teacher/papers/${p.id}/edit`); }} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.white, color: BL, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: font }}>
                                        <FiEdit2 size={13} /> Edit Draft
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleSubmit(p.id); }} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 10, border: 'none', background: BL, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: font }}>
                                        <FiSend size={13} /> Submit
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 10, border: 'none', background: 'rgba(239,68,68,0.08)', color: '#ef4444', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: font }}>
                                        <FiTrash2 size={13} />
                                    </button>
                                </>)}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyPapers;
